import type { ElementDefinition } from "shared/elements-definition";

/**
 * Shape of modules returned by the Vite glob import.
 *
 * Each definition module must export `definition: ElementDefinition`.
 */
interface DefinitionModule {
  readonly definition: ElementDefinition;
}

/**
 * Lazily imported component definition modules.
 *
 * The glob is kept simple so it works in both dev and library builds.
 */
const modules = import.meta.glob<DefinitionModule>("../components/**/*.definition.ts");

/**
 * Converts a component name into a URL-friendly slug.
 *
 * Examples:
 * - "Card" -> "card"
 * - "IconButton" -> "icon-button"
 */
const toSlug = (name: string): string => {
  return name
    .replaceAll(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replaceAll(/\s+/g, "-")
    .toLowerCase();
};

/**
 * All collected {@link ElementDefinition} instances.
 *
 * Starts empty — call {@link initDefinitions} during bootstrap to populate.
 */
const _definitions: ElementDefinition[] = [];
export const elementDefinitions: ReadonlyArray<ElementDefinition> = _definitions;

/**
 * Map of component slug → {@link ElementDefinition}.
 *
 * Starts empty — call {@link initDefinitions} during bootstrap to populate.
 */
const _definitionsBySlug: Record<string, ElementDefinition> = {};
export const elementDefinitionsBySlug: Readonly<Record<string, ElementDefinition>> = _definitionsBySlug;

/**
 * Resolves all definition modules in parallel and populates the
 * {@link elementDefinitions} and {@link elementDefinitionsBySlug} collections.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export const initDefinitions = async (): Promise<void> => {
  if (_definitions.length > 0) return;

  const resolved = await Promise.all(Object.values(modules).map((m) => m()));

  _definitions.push(...resolved.map((m) => m.definition));

  for (const def of _definitions) {
    if (def.componentName) {
      _definitionsBySlug[toSlug(def.componentName)] = def;
    }
  }
};
